// components/charts/ReferralChart.jsx
import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const ReferralChart = ({ data, title = "Referrals Over Time" }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (!chartRef.current) return;

        // Initialize chart
        chartInstance.current = echarts.init(chartRef.current);

        // Process data - group by date
        const dateCountMap = {};
        data.forEach(item => {
            const date = item.createdAt?.split('T')[0] || item.createdAt;
            if (date) {
                dateCountMap[date] = (dateCountMap[date] || 0) + 1;
            }
        });

        // Sort dates and prepare chart data
        const sortedDates = Object.keys(dateCountMap).sort();
        const counts = sortedDates.map(date => dateCountMap[date]);

        const option = {
            title: {
                text: title,
                left: 'center',
                textStyle: {
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#374151'
                }
            },
            tooltip: {
                trigger: 'axis',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderColor: '#e5e7eb',
                borderWidth: 1,
                textStyle: {
                    color: '#374151'
                },
                formatter: (params) => {
                    const date = params[0].axisValue;
                    const value = params[0].value;
                    return `<strong>${date}</strong><br/>Count: ${value}`;
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                top: '15%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: sortedDates,
                axisLabel: {
                    rotate: 45,
                    fontSize: 10,
                    color: '#6b7280'
                },
                axisLine: {
                    lineStyle: {
                        color: '#e5e7eb'
                    }
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    color: '#6b7280'
                },
                axisLine: {
                    lineStyle: {
                        color: '#e5e7eb'
                    }
                },
                splitLine: {
                    lineStyle: {
                        color: '#f3f4f6'
                    }
                }
            },
            series: [
                {
                    name: 'Count',
                    type: 'line',
                    smooth: true,
                    data: counts,
                    lineStyle: {
                        color: '#06b6d4',
                        width: 3
                    },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: 'rgba(6, 182, 212, 0.4)' },
                            { offset: 1, color: 'rgba(6, 182, 212, 0.05)' }
                        ])
                    },
                    itemStyle: {
                        color: '#06b6d4'
                    },
                    symbol: 'circle',
                    symbolSize: 6
                }
            ]
        };

        chartInstance.current.setOption(option);

        // Handle resize
        const handleResize = () => {
            chartInstance.current?.resize();
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            chartInstance.current?.dispose();
        };
    }, [data, title]);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div ref={chartRef} style={{ width: '100%', height: '350px' }} />
            {data.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                    <p className="text-gray-500">No data available</p>
                </div>
            )}
        </div>
    );
};

export default ReferralChart;